import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Menu, X, Shield } from "lucide-react";
import { useState, useEffect } from "react";

// Logo - using the icon logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_taxfaster/artifacts/qy1v0eb1_IMG_7018.png";
// Gold logo for navy background
const LOGO_GOLD = "https://customer-assets.emergentagent.com/job_taxservice-2/artifacts/x1k1tmhp_DE8E8ACF-0019-4648-9B33-EAE1FF441876.png";

export const Header = ({ showAdminLink = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const isIntake = location.pathname === "/intake";
  const isAdmin = location.pathname.startsWith("/admin");
  const isPortal = location.pathname === "/portal";

  // Check if admin is logged in (persisted in sessionStorage)
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('adminAuth');
    setIsAdminLoggedIn(!!adminAuth);
  }, [location.pathname]);

  // Show admin link if: explicitly passed OR admin is logged in
  const shouldShowAdminLink = showAdminLink || isAdminLoggedIn;

  // Brand name color: gold on landing, blue on intake, navy elsewhere
  const getBrandColor = () => {
    if (isLanding) return 'text-[#C9A227]';
    if (isIntake) return 'text-[#121417]';
    return 'text-[#121417]';
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300`}
      style={{ 
        padding: '3px 6px',
        minHeight: '60px',
        background: '#121417'
      }}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Single Row: Logo + Name + CTA + Hamburger */}
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="flex items-center gap-2 min-w-0 flex-shrink" data-testid="header-logo">
              <img 
                src={isLanding ? LOGO_GOLD : LOGO_URL} 
                alt="WMJ Logo" 
                className="h-10 w-auto flex-shrink-0 drop-shadow-lg"
                style={{ objectFit: 'contain' }}
              />
              <span className="font-heading font-bold text-[10px] leading-tight tracking-wide text-white drop-shadow-lg">
                WMJ<br/><span className="text-[#C9A227]">ENTERPRISE</span>
              </span>
            </Link>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to="/intake" data-testid="mobile-cta">
                <Button 
                  className="font-semibold rounded-full text-[8px] px-2 py-1.5 h-7 whitespace-nowrap"
                  style={{
                    backgroundColor: '#C9A227',
                    color: '#121417',
                    fontWeight: '700'
                  }}
                >
                  Start Filing Now
                </Button>
              </Link>
              <button 
                className="p-1"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? (
                  <X className="text-white drop-shadow-lg" size={16} />
                ) : (
                  <Menu className="text-white drop-shadow-lg" size={16} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          {/* Row 1: Logo + Nav */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-5" data-testid="header-logo-desktop">
              <img 
                src={isLanding ? LOGO_GOLD : LOGO_URL} 
                alt="WMJ Enterprise Services Logo" 
                className="h-[90px] w-auto drop-shadow-xl"
                style={{ objectFit: 'contain' }}
              />
              <span className="font-heading font-bold text-xl tracking-wide whitespace-nowrap text-white drop-shadow-lg">
                WMJ <span className="text-[#C9A227]">ENTERPRISE SERVICES</span>
              </span>
            </Link>

            <div className="flex items-center gap-4 ml-auto">
              <nav className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-home"
              >
                Home
              </Link>
              <a 
                href="/#services" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-services"
              >
                Services
              </a>
              <a 
                href="/#about" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-about"
              >
                About
              </a>
              <Link 
                to="/blog" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-blog"
              >
                Blog
              </Link>
              <Link 
                to="/faq" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-faq"
              >
                FAQ
              </Link>
              <Link 
                to="/portal" 
                className="font-medium text-sm transition-colors duration-200 text-white hover:text-[#C9A227] drop-shadow-md"
                data-testid="nav-portal"
              >
                Client Portal
              </Link>
              {shouldShowAdminLink && (
                <Link 
                  to="/admin" 
                  className="font-medium text-sm transition-colors duration-200 text-[#C9A227] hover:text-white drop-shadow-md flex items-center gap-1"
                  data-testid="nav-admin"
                >
                  <Shield size={14} />
                  Admin
                </Link>
              )}
              </nav>
              
              {/* Start Tax Filing Button - Positioned all the way right */}
              <Link to="/intake" data-testid="header-get-started-inline">
                <Button 
                  className="font-bold rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 text-xs"
                  style={{
                    backgroundColor: '#C9A227',
                    color: '#121417',
                    padding: '5px 11px',
                    fontWeight: '700'
                  }}
                >
                  Start Filing Your Tax Now
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-lg shadow-xl mt-4 p-5 absolute left-4 right-4 border border-slate-100">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <a 
                href="/#services" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </a>
              <a 
                href="/#about" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>
              <Link 
                to="/credit-repair" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Credit Repair
              </Link>
              <Link 
                to="/funding" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Funding
              </Link>
              <Link 
                to="/blog" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link 
                to="/faq" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link 
                to="/portal" 
                className="text-[#121417] font-medium hover:text-[#C9A227] py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Client Portal
              </Link>
              {shouldShowAdminLink && (
                <Link 
                  to="/admin" 
                  className="text-[#C9A227] font-medium hover:text-[#121417] py-1 flex items-center gap-2 bg-slate-100 rounded-md px-3"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Shield size={16} />
                  Admin Dashboard
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
